import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  cancelBackfill,
  createBackfill,
  listBackfills,
  listCoverage,
  retryBackfill,
  type BackfillCreate,
  type BackfillSource,
  type BackfillStatus,
  type CoverageRow,
} from "../api/data";
import { listBrokerConnections } from "../api/brokers";
import Card from "../components/Card";
import Button from "../components/Button";
import { ApiError } from "../lib/api";
import { useDebouncedValue } from "../lib/useDebouncedValue";

const CREDENTIALED_SOURCES: BackfillSource[] = ["zerodha", "dhan"];

export default function DataPage() {
  return (
    <div className="space-y-6">
      <h1 className="text-xl sm:text-2xl font-semibold">Historical data</h1>
      <Card title="Coverage (what's in the store)">
        <CoverageTable />
      </Card>
      <Card title="Run a backfill">
        <BackfillForm />
      </Card>
      <Card title="Recent backfill jobs">
        <RecentJobsTable />
      </Card>
    </div>
  );
}

const COVERAGE_PAGE_SIZE = 50;

/** Rows an option contract contributes. Every weekly strike of every expiry
 *  lands here, so on a real store they are the overwhelming majority of the
 *  17k+ rows and drown out the handful of indices, futures and equities the
 *  table is actually consulted for. Hidden by default, one checkbox away. */
function isOptionRow(r: CoverageRow): boolean {
  return r.instrument_type === "CE" || r.instrument_type === "PE";
}

/** The text one row is matched against. Includes the ISO expiry and the
 *  strike, so "24000pe" and "2026-08" both narrow. */
function coverageHaystack(r: CoverageRow): string {
  return [
    r.exchange,
    r.symbol,
    r.instrument_type,
    r.expiry ?? "",
    r.strike ?? "",
    r.timeframe,
  ]
    .join(" ")
    .toUpperCase();
}

function CoverageTable() {
  const { data: rows, isLoading } = useQuery({
    queryKey: ["data-coverage"],
    queryFn: listCoverage,
    // Coverage only changes when a backfill finishes, and this page already
    // polls /backfills at 3s for job status — the fast signal is there. A 10s
    // poll of the full 17,566-row table was pure waste.
    refetchInterval: 60_000,
    staleTime: 55_000,
  });

  const [search, setSearch] = useState("");
  const [showOptions, setShowOptions] = useState(false);
  const [page, setPage] = useState(0);
  // Filtering is local (the rows are already downloaded for the backfill
  // form's covered-check), but a keystroke still re-scans ~17k rows and
  // re-renders — debouncing keeps typing smooth.
  const q = useDebouncedValue(search.trim().toUpperCase(), 200);

  const filtered = useMemo(() => {
    if (!rows) return [];
    const tokens = q ? q.split(/\s+/) : [];
    return rows.filter((r) => {
      if (!showOptions && isOptionRow(r)) return false;
      if (tokens.length === 0) return true;
      const hay = coverageHaystack(r);
      return tokens.every((t) => hay.includes(t));
    });
  }, [rows, q, showOptions]);

  // Any filter change re-slices the list, so the current page number no
  // longer points at the same rows.
  useEffect(() => {
    setPage(0);
  }, [q, showOptions]);

  const hiddenOptions = useMemo(
    () => (rows && !showOptions ? rows.filter(isOptionRow).length : 0),
    [rows, showOptions],
  );

  const pageCount = Math.max(1, Math.ceil(filtered.length / COVERAGE_PAGE_SIZE));
  const safePage = Math.min(page, pageCount - 1);
  const from = safePage * COVERAGE_PAGE_SIZE;
  const visible = filtered.slice(from, from + COVERAGE_PAGE_SIZE);

  if (isLoading) return <div className="text-sm text-muted">Loading…</div>;
  if (!rows || rows.length === 0)
    return (
      <div className="text-sm text-muted">
        Store is empty. Run a backfill below to populate it.
      </div>
    );

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-3">
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search exchange, symbol, expiry, strike, timeframe…"
          className="flex-1 w-full sm:min-w-[240px] text-sm px-3 py-2 rounded border border-border
            bg-surface placeholder:text-muted/70 focus:outline-none focus:ring-1
            focus:ring-brand-500"
        />
        <label className="flex items-center gap-2 text-xs text-fg-subtle whitespace-nowrap">
          <input
            type="checkbox"
            checked={showOptions}
            onChange={(e) => setShowOptions(e.target.checked)}
          />
          Include option contracts
          {hiddenOptions > 0 && (
            <span className="text-muted">({hiddenOptions.toLocaleString()} hidden)</span>
          )}
        </label>
      </div>

      {filtered.length === 0 ? (
        <div className="text-sm text-muted">
          Nothing matches. {!showOptions && hiddenOptions > 0
            ? "Option contracts are hidden — tick the box to include them."
            : ""}
        </div>
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-muted border-b border-border/60">
                <tr>
                  <th className="py-2 pr-3">Exchange</th>
                  <th className="py-2 pr-3">Symbol</th>
                  <th className="py-2 pr-3">Type</th>
                  <th className="py-2 pr-3">Expiry</th>
                  <th className="py-2 pr-3 text-right">Strike</th>
                  <th className="py-2 pr-3">Timeframe</th>
                  <th className="py-2 pr-3 text-right">Bars</th>
                  <th className="py-2 pr-3">First</th>
                  <th className="py-2 pr-3">Last</th>
                </tr>
              </thead>
              <tbody>
                {visible.map((r, i) => (
                  <tr
                    key={`${r.exchange}:${r.symbol}:${r.instrument_type}:${r.expiry ?? ""}:${r.strike ?? ""}:${r.timeframe}:${i}`}
                    className="border-b border-border/60 last:border-0"
                  >
                    <td className="py-2 pr-3 font-mono">{r.exchange}</td>
                    <td className="py-2 pr-3 font-mono">{r.symbol}</td>
                    <td className="py-2 pr-3 font-mono text-xs">{r.instrument_type}</td>
                    <td className="py-2 pr-3 font-mono text-xs">{r.expiry ?? "—"}</td>
                    <td className="py-2 pr-3 font-mono text-xs text-right">
                      {r.strike ?? "—"}
                    </td>
                    <td className="py-2 pr-3 font-mono">{r.timeframe}</td>
                    <td className="py-2 pr-3 font-mono text-right">
                      {r.bar_count.toLocaleString()}
                    </td>
                    <td className="py-2 pr-3 font-mono text-xs">
                      {r.first_ts ? new Date(r.first_ts + "Z").toLocaleString() : "—"}
                    </td>
                    <td className="py-2 pr-3 font-mono text-xs">
                      {r.last_ts ? new Date(r.last_ts + "Z").toLocaleString() : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex items-center justify-between gap-3">
            <div className="text-xs text-muted">
              Showing {from + 1}–{from + visible.length} of{" "}
              {filtered.length.toLocaleString()}
            </div>
            {pageCount > 1 && (
              <div className="flex items-center gap-2">
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={safePage === 0}
                  onClick={() => setPage(Math.max(0, safePage - 1))}
                >
                  ← Prev
                </Button>
                <span className="text-xs text-muted">
                  {safePage + 1} / {pageCount}
                </span>
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={safePage >= pageCount - 1}
                  onClick={() => setPage(safePage + 1)}
                >
                  Next →
                </Button>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function RecentJobsTable() {
  const qc = useQueryClient();
  const { data: jobs, isLoading } = useQuery({
    queryKey: ["backfills"],
    queryFn: () => listBackfills(50),
    refetchInterval: 3_000,
  });
  const cancel = useMutation({
    mutationFn: cancelBackfill,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["backfills"] }),
  });
  const retry = useMutation({
    mutationFn: retryBackfill,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["backfills"] }),
    onError: (e) => {
      alert(
        e instanceof ApiError
          ? `Retry failed: ${String(e.detail ?? e.message)}`
          : "Retry failed (network error)",
      );
    },
  });

  if (isLoading) return <div className="text-sm text-muted">Loading…</div>;
  if (!jobs || jobs.length === 0)
    return <div className="text-sm text-muted">No backfill jobs yet.</div>;

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="text-left text-muted border-b border-border/60">
          <tr>
            <th className="py-2 pr-3">Created</th>
            <th className="py-2 pr-3">Source</th>
            <th className="py-2 pr-3">Instrument</th>
            <th className="py-2 pr-3">Timeframe</th>
            <th className="py-2 pr-3">Range</th>
            <th className="py-2 pr-3">Status</th>
            <th className="py-2 pr-3 text-right">Rows</th>
            <th className="py-2"></th>
          </tr>
        </thead>
        <tbody>
          {jobs.map((j) => {
            const inst = [j.exchange, j.symbol]
              .concat(j.expiry ? [j.expiry] : [])
              .concat(j.strike ? [`${j.strike}${j.instrument_type}`] : [])
              .join(":");
            const range = `${new Date(j.start_ts + "Z").toLocaleDateString()} → ${new Date(
              j.end_ts + "Z",
            ).toLocaleDateString()}`;
            const inFlight = j.status === "PENDING" || j.status === "RUNNING";
            const retryable = j.status === "FAILED" || j.status === "CANCELLED";
            return (
              <tr key={j.id} className="border-b border-border/60 last:border-0">
                <td className="py-2 pr-3 font-mono text-xs">
                  {new Date(j.created_at).toLocaleString()}
                </td>
                <td className="py-2 pr-3 font-mono">{j.source}</td>
                <td className="py-2 pr-3 font-mono">{inst}</td>
                <td className="py-2 pr-3 font-mono">{j.timeframe}</td>
                <td className="py-2 pr-3 font-mono text-xs">{range}</td>
                <td className="py-2 pr-3">
                  <StatusPill status={j.status} />
                  {j.error_message && (
                    <div className="text-xs text-down-700 dark:text-down-400 mt-1 max-w-xs truncate" title={j.error_message}>
                      {j.error_message}
                    </div>
                  )}
                </td>
                <td className="py-2 pr-3 font-mono text-right">
                  {j.rows_written != null ? j.rows_written.toLocaleString() : "—"}
                </td>
                <td className="py-2 text-right whitespace-nowrap">
                  {inFlight && (
                    <Button
                      variant="secondary"
                      size="sm"
                      disabled={cancel.isPending}
                      onClick={() => cancel.mutate(j.id)}
                    >
                      Cancel
                    </Button>
                  )}
                  {retryable && (
                    <Button
                      variant="secondary"
                      size="sm"
                      disabled={retry.isPending}
                      onClick={() => retry.mutate(j.id)}
                    >
                      Retry
                    </Button>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function StatusPill({ status }: { status: BackfillStatus }) {
  const map: Record<BackfillStatus, string> = {
    PENDING: "bg-surface-2 text-fg-subtle",
    RUNNING: "bg-amber-100 dark:bg-warn-500/15 text-warn-700 dark:text-warn-400",
    SUCCESS: "bg-emerald-100 dark:bg-up-500/15 text-up-700 dark:text-up-400",
    FAILED: "bg-red-100 dark:bg-down-500/15 text-down-700 dark:text-down-400",
    CANCELLED: "bg-surface-2 text-fg-subtle",
  };
  return (
    <span className={`inline-block text-xs font-mono px-2 py-0.5 rounded ${map[status]}`}>
      {status}
    </span>
  );
}

function BackfillForm() {
  const qc = useQueryClient();
  const { data: brokers } = useQuery({
    queryKey: ["broker-connections"],
    queryFn: listBrokerConnections,
  });
  // Shared with the CoverageTable query (deduped by react-query). Used by
  // the "Backfill all timeframes" path to skip TFs that already have bars
  // bracketing the requested [start, end] window.
  const { data: coverage } = useQuery({
    queryKey: ["data-coverage"],
    queryFn: listCoverage,
  });

  const [source, setSource] = useState<BackfillSource>("delta");
  const [brokerConnectionId, setBrokerConnectionId] = useState("");
  const [symbol, setSymbol] = useState("");
  const [exchange, setExchange] = useState("DELTA");
  const [instrumentType, setInstrumentType] = useState("PERP");
  const [expiry, setExpiry] = useState("");
  const [strike, setStrike] = useState("");
  const [lotSize, setLotSize] = useState(1);
  const [timeframe, setTimeframe] = useState("1h");
  // When true, ignore `timeframe` and fan out one backfill per supported
  // timeframe. The 1-minute backfill (when usable for the source) is what
  // makes the volume-profile overlay actually useful for intra-candle
  // volume distribution.
  const [allTimeframes, setAllTimeframes] = useState(false);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [volumeSynthesis, setVolumeSynthesis] = useState<
    "none" | "constituents" | "futures"
  >("none");
  const [error, setError] = useState<string | null>(null);
  // Non-error informational banner — used by the "all timeframes" path to
  // tell the user which TFs were skipped because the store already covers
  // [start, end]. Kept separate from `error` so the mutation's onSuccess
  // (which clears errors) doesn't wipe it.
  const [info, setInfo] = useState<string | null>(null);

  // Sensible defaults per source.
  function onSourceChange(next: BackfillSource) {
    setSource(next);
    setError(null);
    if (next === "delta") {
      setExchange("DELTA");
      setInstrumentType("PERP");
      setSymbol("");
    } else if (next === "bhavcopy") {
      setExchange("NSE");
      setInstrumentType("EQ");
      setSymbol("");
    } else if (next === "zerodha" || next === "dhan") {
      setExchange("NSE");
      setInstrumentType("EQ");
      setSymbol("");
    } else if (next === "dukascopy") {
      setExchange("FX");
      setInstrumentType("SPOT");
      setSymbol("");
    }
  }

  const credentialedBrokers = useMemo(
    () => brokers?.filter((b) => b.broker === source) ?? [],
    [brokers, source],
  );

  const create = useMutation({
    mutationFn: createBackfill,
    onSuccess: () => {
      setError(null);
      qc.invalidateQueries({ queryKey: ["backfills"] });
      qc.invalidateQueries({ queryKey: ["data-coverage"] });
    },
    onError: (e) => {
      setError(
        e instanceof ApiError
          ? typeof e.detail === "string"
            ? e.detail
            : JSON.stringify(e.detail)
          : "Failed",
      );
    },
  });

  // Source-aware timeframe matrix for the "Backfill all timeframes" fan-out.
  // Each source only exposes the intervals its upstream API offers, so we must
  // not fan out an interval a source can't serve (the job would just error).
  // Kept in sync with the interval maps in data/sources/*.py:
  //   - zerodha  : 1m/3m/5m/10m/15m/30m/1h/1d (all)
  //   - delta    : no 10m
  //   - dukascopy: no 3m (Dukascopy's library lacks it)
  //   - dhan     : no 3m, no 10m
  //   - bhavcopy : EOD (1d) only
  function supportedTimeframes(s: BackfillSource): string[] {
    switch (s) {
      case "bhavcopy":
        return ["1d"];
      case "zerodha":
        return ["1m", "3m", "5m", "10m", "15m", "30m", "1h", "1d"];
      case "delta":
        return ["1m", "3m", "5m", "15m", "30m", "1h", "1d"];
      case "dukascopy":
        return ["1m", "5m", "10m", "15m", "30m", "1h", "1d"];
      case "dhan":
        return ["1m", "5m", "15m", "30m", "1h", "1d"];
    }
  }

  function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setInfo(null);
    if (!symbol || !startDate || !endDate) {
      setError("Symbol, start, and end are required.");
      return;
    }
    if (CREDENTIALED_SOURCES.includes(source) && !brokerConnectionId) {
      setError(`${source} backfill needs a broker connection.`);
      return;
    }
    const basePayload = (tf: string): BackfillCreate => {
      const p: BackfillCreate = {
        source,
        symbol,
        exchange,
        instrument_type: instrumentType,
        timeframe: tf,
        start: new Date(startDate).toISOString(),
        end: new Date(endDate).toISOString(),
        lot_size: lotSize,
      };
      if (expiry) p.expiry = expiry;
      if (strike) p.strike = strike;
      if (CREDENTIALED_SOURCES.includes(source)) {
        p.broker_connection_id = brokerConnectionId;
      }
      if (instrumentType === "INDEX") {
        if (volumeSynthesis === "constituents") {
          p.synthetic_volume_from_constituents = true;
        } else if (volumeSynthesis === "futures") {
          p.synthetic_volume_from_futures = true;
        }
      }
      return p;
    };
    if (allTimeframes) {
      const all = supportedTimeframes(source);
      const wantedStart = new Date(startDate).getTime();
      const wantedEnd = new Date(endDate).getTime();
      const isCovered = (tf: string): boolean => {
        if (!coverage) return false;
        const row = coverage.find(
          (c) =>
            c.exchange === exchange &&
            c.symbol === symbol &&
            c.timeframe === tf,
        );
        if (!row || !row.first_ts || !row.last_ts) return false;
        const haveStart = new Date(row.first_ts).getTime();
        const haveEnd = new Date(row.last_ts).getTime();
        // Backend `coverage` is a per-TF (first_ts, last_ts) summary, so
        // this can't detect interior gaps — but for the common case of a
        // re-run with the same dates it correctly skips the duplicates.
        return haveStart <= wantedStart && haveEnd >= wantedEnd;
      };
      const toRun = all.filter((tf) => !isCovered(tf));
      const skipped = all.filter((tf) => isCovered(tf));
      if (toRun.length === 0) {
        setInfo(null);
        setError(
          `All ${all.length} timeframes (${all.join(", ")}) already cover this window. Nothing to do.`,
        );
        return;
      }
      setInfo(
        skipped.length > 0
          ? `Queued ${toRun.length} backfill${toRun.length === 1 ? "" : "s"} (${toRun.join(", ")}). Skipped already-covered: ${skipped.join(", ")}.`
          : `Queued ${toRun.length} backfill${toRun.length === 1 ? "" : "s"} (${toRun.join(", ")}).`,
      );
      for (const tf of toRun) {
        create.mutate(basePayload(tf));
      }
      return;
    }
    setInfo(null);
    create.mutate(basePayload(timeframe));
  }

  const showExpiry = ["FUT", "CE", "PE"].includes(instrumentType);
  const showStrike = ["CE", "PE"].includes(instrumentType);

  return (
    <form onSubmit={onSubmit} className="space-y-3">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label="Source">
          <select
            value={source}
            onChange={(e) => onSourceChange(e.target.value as BackfillSource)}
            className="w-full border border-border rounded px-3 py-2"
          >
            <option value="delta">Delta Exchange India (no creds)</option>
            <option value="bhavcopy">NSE bhavcopy (EOD, free)</option>
            <option value="zerodha">Zerodha (needs connection)</option>
            <option value="dhan">Dhan (needs connection)</option>
            <option value="dukascopy">Dukascopy (forex, no creds)</option>
          </select>
        </Field>
        {CREDENTIALED_SOURCES.includes(source) && (
          <Field label="Broker connection">
            <select
              value={brokerConnectionId}
              onChange={(e) => setBrokerConnectionId(e.target.value)}
              className="w-full border border-border rounded px-3 py-2"
            >
              <option value="">— pick one —</option>
              {credentialedBrokers.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.broker}/{b.label}
                </option>
              ))}
            </select>
          </Field>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        <Field label="Symbol">
          <input
            value={symbol}
            onChange={(e) => setSymbol(e.target.value)}
            placeholder={
              source === "delta"
                ? "BTCUSD"
                : source === "bhavcopy"
                  ? "RELIANCE"
                  : source === "dukascopy"
                    ? "EURUSD"
                    : "NIFTY 50"
            }
            className="w-full border border-border rounded px-3 py-2 font-mono"
            required
          />
        </Field>
        <Field label="Exchange">
          <select
            value={exchange}
            onChange={(e) => setExchange(e.target.value)}
            className="w-full border border-border rounded px-3 py-2"
          >
            <option>NSE</option>
            <option>BSE</option>
            <option>NFO</option>
            <option>BFO</option>
            <option>DELTA</option>
            <option>BINANCE</option>
            <option>FX</option>
          </select>
        </Field>
        <Field label="Type">
          <select
            value={instrumentType}
            onChange={(e) => setInstrumentType(e.target.value)}
            className="w-full border border-border rounded px-3 py-2"
          >
            <option>EQ</option>
            <option>FUT</option>
            <option>CE</option>
            <option>PE</option>
            <option>INDEX</option>
            <option>PERP</option>
            <option>SPOT</option>
          </select>
        </Field>
      </div>

      {(showExpiry || showStrike) && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {showExpiry && (
            <Field label="Expiry (YYYY-MM-DD)">
              <input
                value={expiry}
                onChange={(e) => setExpiry(e.target.value)}
                placeholder="2026-05-29"
                className="w-full border border-border rounded px-3 py-2 font-mono"
              />
            </Field>
          )}
          {showStrike && (
            <Field label="Strike">
              <input
                value={strike}
                onChange={(e) => setStrike(e.target.value)}
                placeholder="24000"
                className="w-full border border-border rounded px-3 py-2 font-mono"
              />
            </Field>
          )}
          {(instrumentType === "FUT" || instrumentType === "CE" || instrumentType === "PE") && (
            <Field label="Lot size">
              <input
                type="number"
                value={lotSize}
                min={1}
                onChange={(e) => setLotSize(parseInt(e.target.value || "1"))}
                className="w-full border border-border rounded px-3 py-2 font-mono"
              />
            </Field>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        <Field label="Timeframe">
          <select
            value={timeframe}
            onChange={(e) => setTimeframe(e.target.value)}
            disabled={allTimeframes}
            className="w-full border border-border rounded px-3 py-2 disabled:bg-surface-2 disabled:text-muted"
          >
            <option>1m</option>
            <option>3m</option>
            <option>5m</option>
            <option>10m</option>
            <option>15m</option>
            <option>30m</option>
            <option>1h</option>
            <option>1d</option>
          </select>
          <label className="mt-2 flex items-start gap-2 text-xs text-fg-subtle">
            <input
              type="checkbox"
              checked={allTimeframes}
              onChange={(e) => setAllTimeframes(e.target.checked)}
              className="mt-0.5"
            />
            <span>
              <span className="font-medium text-fg-subtle">
                Backfill all timeframes
              </span>{" "}
              — fires one job per supported TF for this source (
              {supportedTimeframes(source).join(", ")}). The 1-minute backfill
              is what makes the volume-profile overlay actually distribute
              volume across price rows within a single candle.
            </span>
          </label>
        </Field>
        <Field label="From (date)">
          <input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            className="w-full border border-border rounded px-3 py-2"
            required
          />
        </Field>
        <Field label="To (date)">
          <input
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            className="w-full border border-border rounded px-3 py-2"
            required
          />
        </Field>
      </div>

      {source === "dukascopy" && (
        <div className="bg-surface-2 border border-border rounded px-3 py-2 text-xs text-fg-subtle space-y-1">
          <div className="font-medium text-fg">Dukascopy spot forex notes</div>
          <div>
            Symbols are 6-letter pairs like <span className="font-mono">EURUSD</span>,{" "}
            <span className="font-mono">GBPJPY</span>, <span className="font-mono">XAUUSD</span>.
            History on majors goes back to ~2003. Bars are bid-side; weekends have no
            bars (market closed). Volume is Dukascopy's own SWFX pool volume — a
            well-correlated proxy (~95%+), not consolidated market volume, since spot
            forex has no central tape.
          </div>
        </div>
      )}

      {instrumentType === "INDEX" && (
        <div className="bg-surface-2 border border-border rounded px-3 py-2 space-y-2">
          <div className="text-xs font-medium text-fg-subtle">
            Volume synthesis (NSE doesn't publish volume for indices)
          </div>
          <label className="flex items-start gap-2">
            <input
              type="radio"
              name="vol-synthesis"
              checked={volumeSynthesis === "none"}
              onChange={() => setVolumeSynthesis("none")}
              className="mt-0.5"
            />
            <span className="text-xs text-fg-subtle">
              <span className="font-medium">None.</span> Store volume=0 (raw
              NSE data). Volume profile uses time-based fallback; volume pane
              shows empty.
            </span>
          </label>
          <label className="flex items-start gap-2">
            <input
              type="radio"
              name="vol-synthesis"
              checked={volumeSynthesis === "constituents"}
              onChange={() => setVolumeSynthesis("constituents")}
              className="mt-0.5"
            />
            <span className="text-xs text-fg-subtle">
              <span className="font-medium">
                From constituent stocks (matches TradingView).
              </span>{" "}
              Sums cash-market volume across the index's constituent NSE EQ
              stocks per timestamp. Right choice for most index strategies
              and what TradingView displays. Heavier — NIFTY 50 fetches 50
              symbols; multi-year 1h backfills take a few minutes.
            </span>
          </label>
          <label className="flex items-start gap-2">
            <input
              type="radio"
              name="vol-synthesis"
              checked={volumeSynthesis === "futures"}
              onChange={() => setVolumeSynthesis("futures")}
              className="mt-0.5"
            />
            <span className="text-xs text-fg-subtle">
              <span className="font-medium">From futures contracts.</span>{" "}
              Sums volume across active NSE F&O monthly futures contracts.
              Useful for derivatives-focused strategies but a different
              number from what TradingView shows. Requires
              instruments_history snapshots covering the window.
            </span>
          </label>
        </div>
      )}

      {error && (
        <div className="text-sm text-down-700 dark:text-down-400 bg-down-50 dark:bg-down-500/10 border border-down-200 dark:border-down-400/30 rounded px-3 py-2">
          {error}
        </div>
      )}
      {info && (
        <div className="text-sm text-up-700 dark:text-up-400 bg-up-50 dark:bg-up-500/10 border border-up-200 dark:border-up-400/30 rounded px-3 py-2">
          {info}
        </div>
      )}

      <div className="flex gap-2">
        <Button type="submit" disabled={create.isPending}>
          {create.isPending ? "Submitting…" : "Start backfill"}
        </Button>
        <Button
          type="button"
          variant="secondary"
          disabled={create.isPending}
          onClick={() => {
            setSymbol("");
            setExpiry("");
            setStrike("");
            setLotSize(1);
            setStartDate("");
            setEndDate("");
            setBrokerConnectionId("");
            setError(null);
          }}
        >
          Clear form
        </Button>
      </div>
    </form>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="block text-sm font-medium text-fg-subtle mb-1">{label}</label>
      {children}
    </div>
  );
}
